import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, RefreshCw, Send, Trash2 } from 'lucide-react';
import { FEEDBACK_AREAS, FEEDBACK_KINDS, FEEDBACK_SEVERITIES, FEEDBACK_STATUSES, deleteFeedback, sendFeedback, updateFeedback } from '@mkcore/dev/schema';
import { Badge, Button, Card, ErrorState, Field, Input, Mono, NativeSelect, PageHeader, Skeleton, Textarea, toast } from '@mkcore/ui';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { feedbackConfigQuery, feedbackQuery } from '@/features/feedback/queries';
import { KindLabel, SeverityText, StatusBadge } from '@/features/feedback/labels';
import { ConfirmButton } from '@/components/confirm';

export const Route = createFileRoute('/_app/feedback/$id')({ component: FeedbackDetail });

function Block({ label, text }: { label: string; text?: string | null }) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-0.5 text-sm font-bold">{label}</div>
      <p className={text ? 'whitespace-pre-wrap text-sm' : 'text-sm text-fg-3'}>{text || t('feedback.detail.notGiven')}</p>
    </div>
  );
}

/** One report: the words, the screenshot, the Forgejo issue with its comments, triage and the technical details. */
function FeedbackDetail() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const fb = useQuery(feedbackQuery(id));
  const config = useQuery(feedbackConfigQuery);
  const manager = useCan('feedback:read');
  const canUpdate = useCan('feedback:update');
  const canDelete = useCan('feedback:delete');
  const [edit, setEdit] = useState<{ status: string; kind: string; severity: string; area: string; title: string; triageNote: string } | null>(null);
  useEffect(() => setEdit(null), [id, fb.data?.version]);

  const invalidate = [['dev', 'feedback']];
  const save = useActionMutation(updateFeedback, { invalidate, success: t('feedback.triage.saved') });
  const send = useActionMutation(sendFeedback, {
    invalidate,
    onSuccess: (r) => {
      if (r.forge.warning) toast.warning(r.forge.warning);
    },
  });
  const remove = useActionMutation(deleteFeedback, { invalidate, success: t('feedback.delete.done'), onSuccess: () => void navigate({ to: '/feedback' }) });

  if (fb.isError) return <ErrorState title={t('errors.generic')} description={message(fb.error)} />;
  if (!fb.data) return <Skeleton className="h-96" />;
  const f = fb.data;
  const tracked = f.forge.number !== null;
  const forgeReady = !!config.data?.forge.configured;
  const e = edit ?? { status: f.status, kind: f.kind, severity: f.severity ?? 'minor', area: f.area, title: f.title, triageNote: f.triageNote ?? '' };
  const setE = (patch: Partial<typeof e>) => setEdit({ ...e, ...patch });
  const problems = f.consoleLog.filter((c) => c.level === 'error' || c.level === 'warn').length;
  const detailsIncluded = f.context.detailsIncluded !== false;

  return (
    <>
      <Link to="/feedback" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('feedback.title')}
      </Link>
      <PageHeader
        eyebrow={f.ref}
        title={f.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={f.status} />
            <KindLabel kind={f.kind} />
            {f.severity && <SeverityText severity={f.severity} />}
            {f.source === 'forge' && <Badge tone="outline">{t('feedback.source.forge')}</Badge>}
          </span>
        }
        actions={
          <>
            {manager && (
              <Button
                variant="secondary"
                loading={send.isPending}
                disabled={!forgeReady}
                title={forgeReady ? undefined : t('feedback.forge.notConnected')}
                onClick={() => send.mutate({ input: { id } })}
              >
                {tracked ? <RefreshCw /> : <Send />} {t(tracked ? 'feedback.forge.readBack' : 'feedback.forge.send')}
              </Button>
            )}
            {canDelete && !tracked && (
              <ConfirmButton
                variant="danger"
                icon={<Trash2 />}
                label={t('feedback.delete.label')}
                title={t('feedback.delete.title')}
                description={t('feedback.delete.text')}
                onConfirm={() => remove.mutateAsync({ input: { id }, ifMatch: f.version })}
              />
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="grid content-start gap-5">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-fg-3">{t('feedback.detail.reportedBy')}</dt>
            <dd>{f.reporterLabel}</dd>
            <dt className="text-fg-3">{t('feedback.detail.reportedAt')}</dt>
            <dd className="tabular">{formatDateTime(f.createdAt)}</dd>
            <dt className="text-fg-3">{t('feedback.fields.area')}</dt>
            <dd>{t(`feedback.area.${f.area}`)}</dd>
            {f.page && (
              <>
                <dt className="text-fg-3">{t('feedback.detail.page')}</dt>
                <dd>
                  <Mono className="break-all text-xs">{f.page}</Mono>
                </dd>
              </>
            )}
          </dl>
          {f.kind === 'bug' && f.source === 'app' ? (
            <>
              <Block label={t('feedback.fields.repro')} text={f.repro} />
              <div className="grid gap-4 sm:grid-cols-2">
                <Block label={t('feedback.fields.expected')} text={f.expected} />
                <Block label={t('feedback.fields.actual')} text={f.actual} />
              </div>
              {f.description && <Block label={t('feedback.fields.more')} text={f.description} />}
            </>
          ) : (
            <div>
              <div className="mb-0.5 text-sm font-bold">{t(f.source === 'forge' ? 'feedback.detail.descriptionForge' : 'feedback.fields.description')}</div>
              <p className={f.description ? 'whitespace-pre-wrap text-sm' : 'text-sm text-fg-3'}>{f.description || t('feedback.detail.noDescription')}</p>
            </div>
          )}
          {f.hasScreenshot && (
            <div>
              <div className="mb-1 text-sm font-bold">{t('feedback.screenshot.title')}</div>
              <a href={`/api/v1/dev/feedback/${f.id}/screenshot`} target="_blank" rel="noreferrer">
                <img src={`/api/v1/dev/feedback/${f.id}/screenshot`} alt={t('feedback.screenshot.alt')} className="max-h-96 rounded-sm border border-line object-contain" />
              </a>
            </div>
          )}
          {manager && f.comments.length > 0 && (
            <div>
              <div className="mb-1 text-sm font-bold">{t('feedback.detail.comments')}</div>
              <div className="grid gap-2">
                {f.comments.map((c) => (
                  <Card key={c.id} className="p-3">
                    <div className="flex justify-between gap-2 text-xs">
                      <span className="font-bold">{c.author}</span>
                      <span className="tabular text-fg-3">{formatDateTime(c.createdAt)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{c.body.replace(/<!--[\s\S]*?-->/g, '').trim()}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}
          {f.source === 'app' && (
            <div className="grid gap-2">
              <details className="rounded-md border border-line-subtle p-3">
                <summary className="cursor-pointer text-sm font-bold">{t('feedback.detail.context')}</summary>
                {!detailsIncluded && <p className="mt-2 text-xs text-fg-3">{t('feedback.details.leftOut')}</p>}
                <table className="mt-2 text-xs">
                  <tbody>
                    {Object.entries(f.context).map(([k, v]) => (
                      <tr key={k}>
                        <td className="pr-4 text-fg-3">{k}</td>
                        <td className="break-all">{String(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
              <details className="rounded-md border border-line-subtle p-3">
                <summary className="cursor-pointer text-sm font-bold">{t('feedback.detail.console', { n: f.consoleLog.length, p: problems })}</summary>
                {f.consoleLog.length === 0 ? (
                  <p className="mt-2 text-xs text-fg-3">{t('feedback.detail.nothing')}</p>
                ) : (
                  <div className="mt-2 grid gap-0.5 font-mono text-[11px]">
                    {f.consoleLog.map((c, i) => (
                      <div key={i} className={c.level === 'error' ? 'text-danger' : c.level === 'warn' ? 'text-warning' : undefined} style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                        {c.t.slice(11, 19)} {c.level}
                        {c.source && c.source !== 'console' ? ` (${c.source})` : ''}: {c.message}
                      </div>
                    ))}
                  </div>
                )}
              </details>
              <details className="rounded-md border border-line-subtle p-3">
                <summary className="cursor-pointer text-sm font-bold">{t('feedback.detail.session', { n: f.sessionLog.length })}</summary>
                {f.sessionLog.length === 0 ? (
                  <p className="mt-2 text-xs text-fg-3">{t('feedback.detail.nothing')}</p>
                ) : (
                  <div className="mt-2 grid gap-0.5 font-mono text-[11px]">
                    {f.sessionLog.map((s, i) => {
                      const failed = s.kind === 'api' && (s.status ?? 0) >= 400;
                      const what =
                        s.kind === 'nav'
                          ? `${t('feedback.detail.opened')} ${s.path}`
                          : s.kind === 'api'
                            ? `${s.method} ${s.path} → ${s.status || t('feedback.detail.noAnswer')} (${s.ms} ms)`
                            : s.kind === 'click'
                              ? `${t('feedback.detail.pressed')} ${s.label}`
                              : `${s.kind}: ${s.label ?? ''}`;
                      return (
                        <div key={i} className={failed || s.kind === 'error' ? 'text-danger' : undefined} style={{ wordBreak: 'break-word' }}>
                          {s.t.slice(11, 19)} {what}
                        </div>
                      );
                    })}
                  </div>
                )}
              </details>
            </div>
          )}
        </div>

        <div className="grid content-start gap-4">
          {manager && (
            <Card className="p-4">
              <div className="mb-1 text-sm font-bold">{t('feedback.forge.title')}</div>
              {tracked ? (
                <p className="text-sm">
                  <a href={f.forge.url ?? undefined} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold hover:underline">
                    {t('feedback.forge.issue', { n: f.forge.number })} <ExternalLink className="size-3" />
                  </a>{' '}
                  {t(f.forge.state === 'closed' ? 'feedback.forge.closed' : 'feedback.forge.open')}, {t('feedback.forge.comments', { n: f.forge.comments })}
                </p>
              ) : (
                <p className="text-sm text-fg-3">{t('feedback.forge.notYet')}</p>
              )}
              {f.forge.labels.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {f.forge.labels.map((l) => (
                    <Badge key={l} tone="outline">
                      {l}
                    </Badge>
                  ))}
                </div>
              )}
              {f.forge.syncedAt && <p className="mt-2 text-xs text-fg-3">{t('feedback.forge.lastRead', { when: formatDateTime(f.forge.syncedAt) })}</p>}
              {f.forge.attempts > 0 && !tracked && <p className="mt-1 text-xs text-fg-3">{t('feedback.forge.attempts', { n: f.forge.attempts })}</p>}
              {f.forge.warning && <p className="mt-2 text-xs text-warning">{f.forge.warning}</p>}
              {f.forge.error && (
                <p className="mt-2 text-xs text-danger" role="alert">
                  {f.forge.error}
                </p>
              )}
              {!forgeReady && <p className="mt-2 text-xs text-fg-3">{t('feedback.forge.notConnected')}</p>}
            </Card>
          )}
          {canUpdate && (
            <Card className="grid gap-3 p-4">
              <div>
                <div className="text-sm font-bold">{t('feedback.triage.title')}</div>
                {tracked && <p className="text-xs text-fg-3">{t('feedback.triage.hint')}</p>}
              </div>
              <Field label={t('common.status')}>
                {(p) => (
                  <NativeSelect {...p} value={e.status} onChange={(ev) => setE({ status: ev.target.value })}>
                    {FEEDBACK_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`feedback.status.${s}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('feedback.kindLabel')}>
                {(p) => (
                  <NativeSelect {...p} value={e.kind} onChange={(ev) => setE({ kind: ev.target.value })}>
                    {FEEDBACK_KINDS.map((k) => (
                      <option key={k} value={k}>
                        {t(`feedback.kind.${k}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              {e.kind === 'bug' && (
                <Field label={t('feedback.fields.severity')}>
                  {(p) => (
                    <NativeSelect {...p} value={e.severity} onChange={(ev) => setE({ severity: ev.target.value })}>
                      {FEEDBACK_SEVERITIES.map((s) => (
                        <option key={s} value={s}>
                          {t(`feedback.severity.${s}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
              )}
              <Field label={t('feedback.fields.area')}>
                {(p) => (
                  <NativeSelect {...p} value={e.area} onChange={(ev) => setE({ area: ev.target.value })}>
                    {FEEDBACK_AREAS.map((a) => (
                      <option key={a} value={a}>
                        {t(`feedback.area.${a}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('feedback.fields.title')}>{(p) => <Input {...p} value={e.title} maxLength={200} onChange={(ev) => setE({ title: ev.target.value })} />}</Field>
              <Field label={t('feedback.triage.note')} hint={t('feedback.triage.noteHint')}>
                {(p) => <Textarea {...p} rows={3} value={e.triageNote} onChange={(ev) => setE({ triageNote: ev.target.value })} />}
              </Field>
              <div className="flex items-center justify-between gap-2">
                {tracked ? <span className="text-xs text-fg-3">{t('feedback.delete.tracked')}</span> : <span />}
                <Button
                  size="sm"
                  disabled={!edit}
                  loading={save.isPending}
                  onClick={() =>
                    save.mutate({
                      input: {
                        id,
                        status: e.status as never,
                        kind: e.kind as never,
                        severity: e.kind === 'bug' ? (e.severity as never) : null,
                        area: e.area as never,
                        title: e.title.trim(),
                        triageNote: e.triageNote.trim() || null,
                      },
                      ifMatch: f.version,
                    })
                  }
                >
                  {t('common.save')}
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
